// services/contact.service.js
import { gql } from 'graphql-tag';
import client from '../apollo-client';

/**
 * `sendContactEmail` — the existing AppSync mutation that fronts the
 * `zn-<env>-web-app-contact-us-function` Lambda. It sends the message to
 * contact@zenitailab.com via SES and a confirmation back to the sender.
 *
 * This is the same path the marketing site's contact form uses, so we
 * reuse the app's Apollo client (AppSync endpoint + API key) rather than
 * `mailto:` (which never actually delivers) or a new backend.
 */
const SEND_CONTACT_EMAIL = gql`
  mutation SendContactEmail(
    $name: String!
    $lastName: String!
    $email: String!
    $reason: String!
    $message: String!
    $phone: String
    $company: String
    $address: String
  ) {
    sendContactEmail(
      name: $name
      lastName: $lastName
      email: $email
      reason: $reason
      message: $message
      phone: $phone
      company: $company
      address: $address
    ) {
      success
      message
    }
  }
`;

class ContactService {
  /**
   * Send a contact email through AppSync/SES.
   *
   * @param {Object} params
   * @param {string} params.name    - full name (split into name/lastName)
   * @param {string} params.email
   * @param {string} params.reason  - e.g. "Talk to a human", "Get more coins"
   * @param {string} params.message
   * @param {string} [params.phone]
   * @param {string} [params.company]
   * @returns {Promise<{success: boolean, message: string}>}
   */
  async sendContactEmail({
    name,
    email,
    reason,
    message,
    phone = '',
    company = '',
    address = '',
  }) {
    try {
      // The mutation requires name + lastName; the forms collect a single
      // full name, so split on the first space (last name may be empty).
      const full = (name || '').trim();
      const parts = full.split(/\s+/).filter(Boolean);
      const firstName = parts[0] || full || '—';
      const lastName = parts.slice(1).join(' ');

      const { data } = await client.mutate({
        mutation: SEND_CONTACT_EMAIL,
        variables: {
          name: firstName,
          lastName,
          email: (email || '').trim(),
          reason: reason || 'Contact',
          message: (message || '').trim(),
          phone: phone || '',
          company: company || '',
          address: address || '',
        },
        fetchPolicy: 'no-cache',
      });

      const res = data?.sendContactEmail;
      if (res?.success) {
        return { success: true, message: res.message || 'Sent' };
      }
      return {
        success: false,
        message: res?.message || 'We could not send your message.',
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.message || 'Network error while sending your message.',
      };
    }
  }
}

const contactService = new ContactService();
export default contactService;
